import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, FilterIcon, Link2Icon, ZapIcon, GitBranchIcon, BarChart3Icon, ArrowRightIcon, ArrowDownIcon, UserCheckIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { atnaTransformCopy as c, transformSteps, type Bi, type TransformKey } from '../../data/atnaTransform';
type Pick = (b: Bi) => string;
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const ICONS: Record<TransformKey, typeof SearchIcon> = {
  analyze: SearchIcon,
  simplify: FilterIcon,
  connect: Link2Icon,
  automate: ZapIcon,
  orchestrate: GitBranchIcon,
  measure: BarChart3Icon
};

/* Small artifact under each step — only SIMPLIFIER and ORCHESTRER carry a message. */
function Artifact({
  k,
  pick
}: {
  k: TransformKey;
  pick: Pick;
}) {
  if (k === 'simplify') {
    return <div className="flex flex-col items-center gap-1.5 w-full" data-id="element-1006" data-mp="x68emjcps5ro">
        <div className="grid grid-cols-2 gap-1 w-full" data-id="element-1007" data-mp="1168vsbr76v2f">
          {c.simplifyBefore.map((s, i) => <span key={s.fr} className="rounded-md border border-dashed border-[#D6D8E2] bg-white px-1 py-0.5 text-center font-sans text-[10.5px] text-[#8A8FA3] leading-tight" style={{
          opacity: 1 - i * 0.16
        }} data-id="element-1008" data-mp="n7938t0kdw0z" data-mpi={i}>
              {pick(s)}
            </span>)}
        </div>
        <ArrowDownIcon className="w-3 h-3 text-[#6D66F0]" strokeWidth={2} aria-hidden="true" data-id="element-1009" data-mp="i8fxxnvflbbw" />
        <span className="w-full rounded-md bg-[#EEEDFD] px-2 py-0.5 text-center font-sans text-[11px] font-medium text-[#4F46E5]" data-id="element-1010" data-mp="4hj8u70prc9l">
          {pick(c.simplifyAfter)}
        </span>
      </div>;
  }
  if (k === 'orchestrate') {
    return <p className="flex items-start gap-1.5 rounded-md bg-[#F4FAF6] px-2 py-1.5 text-left font-sans text-[11px] leading-snug text-[#2A3150]" data-id="element-1011" data-mp="3qpspnewka7nv">
        <UserCheckIcon className="w-3.5 h-3.5 text-[#16A34A] shrink-0 mt-px" strokeWidth={2} aria-hidden="true" data-id="element-1012" data-mp="3cize6py1u910" />
        <span data-id="element-1013" data-mp="37qfst6t4e9ln">{c.control.map(t => <span key={t.fr} className="block" data-id="element-1014" data-mp="37p4n31g46rrw">{pick(t)}</span>)}</span>
      </p>;
  }
  return null;
}
function StepCard({
  s,
  n,
  pick
}: {
  s: (typeof transformSteps)[number];
  n: number;
  pick: Pick;
}) {
  const Icon = ICONS[s.key];
  return <article className="h-full rounded-2xl bg-white border border-[#EEEDF7] shadow-[0_12px_30px_-26px_rgba(18,26,58,0.3)] px-4 pt-4 pb-4 flex flex-col items-center text-center" data-id="element-1015" data-mp="3vhboif0p5oy6">
      <span className="w-10 h-10 rounded-full bg-[#F3F2FD] flex items-center justify-center" data-id="element-1016" data-mp="3lim4ogp8pufa">
        <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1017" data-mp="3gjjcvxy9tkp9" />
      </span>
      <span className="mt-2.5 font-sans text-[10.5px] font-medium text-[#8A8FB8] tabular-nums" data-id="element-1018" data-mp="2a37blo05jmpg">{String(n).padStart(2, '0')}</span>
      <h3 className="mt-0.5 font-sans text-[12.5px] font-semibold tracking-[0.12em] uppercase text-[#121A3A]" data-id="element-1019" data-mp="253b43es8xd55">{pick(s.label)}</h3>
      <p className="mt-1.5 font-sans text-[13px] text-[#4A5268] leading-snug" data-id="element-1020" data-mp="ko4y39271t69">{pick(s.desc)}</p>
      <div className="mt-auto pt-3 w-full" data-id="element-1021" data-mp="plod4b6pu9z9">
        <Artifact k={s.key} pick={pick} data-id="element-1022" data-mp="ukrf1mreg030" />
      </div>
    </article>;
}
export function AtnaTransform() {
  const {
    lang
  } = useLanguage();
  const pick: Pick = b => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-[#FDFDFF] py-24 lg:py-28 px-6" aria-labelledby="atna-transform-title" data-id="element-1023" data-mp="14b2mhjaakren">
      <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[1000px] h-[520px] rounded-full bg-[#EEEDFD] opacity-60 blur-[160px]" aria-hidden="true" data-id="element-1024" data-mp="18al5uamgsufd" />

      <div className="relative max-w-[1440px] mx-auto" data-id="element-1025" data-mp="1iac4moi25vl9">
        <motion.div {...reveal} className="grid grid-cols-1 xl:grid-cols-[50fr_50fr] gap-8 xl:gap-16 items-end" data-id="element-1026" data-mp="1i7px2ov63g31">
          <div data-id="element-1027" data-mp="3k1fbd2otuq0j">
            <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-1028" data-mp="3k0xa1rnb5llr">{pick(c.eyebrow)}</p>
            <h2 id="atna-transform-title" className="font-serif text-[2.2rem] sm:text-[2.8rem] lg:text-[3.1rem] leading-[1.02] tracking-tight mt-5" data-id="element-1029" data-mp="1ebli4fsqtuqn">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-1030" data-mp="1ncjkktnj9u74">{pick(l.text)}</span>)}
            </h2>
          </div>
          <p className="font-sans text-[15.5px] md:text-[16.5px] text-[#4A5268] leading-relaxed max-w-[560px] xl:pb-2" data-id="element-1031" data-mp="vlvq20gd7n2f">{pick(c.body)}</p>
        </motion.div>

        <motion.div {...reveal} className="mt-16 lg:mt-20" data-id="element-1032" data-mp="qlr783rzig91">
          {/* Desktop: one horizontal journey */}
          <ol className="hidden xl:flex items-stretch" data-id="element-1033" data-mp="zoa7qh8asgs4">
            {transformSteps.map((s, i) => <li key={s.key} className="flex items-stretch flex-1 min-w-0" data-id="element-1034" data-mp="byfva0rw4u8e" data-mpi={i}>
                <div className="flex-1 min-w-0" data-id="element-1035" data-mp="bwlbipscv85w"><StepCard s={s} n={i + 1} pick={pick} data-id="element-1036" data-mp="7xm7r8id1hg1" /></div>
                {i < transformSteps.length - 1 && <span className="w-7 shrink-0 flex items-center justify-center" aria-hidden="true" data-id="element-1037" data-mp="3tya5a0dfym25">
                    <ArrowRightIcon className="w-3.5 h-3.5 text-[#6D66F0]" strokeWidth={1.8} data-id="element-1038" data-mp="2kxkswbiei5j" />
                  </span>}
              </li>)}
          </ol>
          {/* Tablet & mobile */}
          <ol className="xl:hidden grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5" data-id="element-1039" data-mp="3p08h5115rwjt">
            {transformSteps.map((s, i) => <li key={s.key} data-id="element-1040" data-mp="ghsy50ysmvre" data-mpi={i}><StepCard s={s} n={i + 1} pick={pick} data-id="element-1041" data-mp="ligmfevn0yhx" /></li>)}
          </ol>
        </motion.div>

        <motion.p {...reveal} className="mt-16 lg:mt-20 text-center font-serif text-[1.7rem] sm:text-[2rem] lg:text-[2.2rem] leading-[1.15] tracking-tight" data-id="element-1042" data-mp="6lz9wkbppdts">
          {c.outcome.map(l => <span key={l.text.fr} className={`block lg:inline ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-1043" data-mp="2rbyeqkxgn6s6">
              {pick(l.text)}{' '}
            </span>)}
        </motion.p>
      </div>
    </section>;
}